import {
  Component,
  forwardRef,
  inject,
  OnDestroy,
  OnInit,
  Signal,
  effect,
  signal,
  computed,
  ViewChild,
  linkedSignal,
  input,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { startWith } from 'rxjs/operators';
import { FormBuilder, FormsModule, ReactiveFormsModule, NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Subscription, from } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  switchMap,
  tap,
} from 'rxjs/operators';
import { LucideIconComponent } from '../../lucide-icon/lucide-icon.component';

import { GoogleMapsService } from '../../../../core/services/google-maps.service';
import { LayoutService } from '../../../../core/services/layout.service';
import { InputComponent } from '../input/input.component';
import { FormFieldComponent } from '../form-field/form-field.component';
import { Address } from '../../../../core/models/address.model';
import { AddressPredictionsComponent } from './address-predictions/address-predictions.component';
import { AddressDisplayComponent } from './address-display/address-display.component';
import { SelectComponent, SelectOption } from '../select/select.component';
import { ADDRESS_DATA, State } from './address-data';

@Component({
  selector: 'app-address-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    InputComponent,
    FormFieldComponent,
    AddressPredictionsComponent,
    LucideIconComponent,
    AddressDisplayComponent,
    SelectComponent,
  ],
  templateUrl: './address-form.component.html',
  styleUrls: ['./address-form.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AddressFormComponent),
      multi: true,
    },
  ],
})
export class AddressFormComponent
  implements ControlValueAccessor, OnInit, OnDestroy
{
  // --- Injected Services ---
  private readonly googleMapsService = inject(GoogleMapsService);
  private readonly fb = inject(FormBuilder);
  private readonly layoutService = inject(LayoutService);
  private subs = new Subscription();

  @ViewChild('streetInput', { read: InputComponent, static: false })
  private streetInput?: InputComponent;

  readonly stateDisplayFormat = input<'full' | 'short'>('full');

  // --- State Management ---
  protected predictions = signal<google.maps.places.PlacePrediction[]>([]);
  protected isLoading = signal(false);
  protected hasFocus = signal(false);
  private sessionToken: google.maps.places.AutocompleteSessionToken | undefined;

  protected isMobile = this.layoutService.isTabletOrBelow;
  protected isOverlayVisible = signal(false);
  private draftAddress = signal<Address | null>(null);

  // 3. Add data for the new select inputs
  protected readonly countryOptions: SelectOption[] =
    ADDRESS_DATA.countries.map((c) => ({ label: c, value: c }));
  protected stateAndProvinceOptions = signal<SelectOption[]>([]);

  // --- Form & CVA ---
  protected addressForm = this.fb.group({
    placeId: this.fb.control<string | null>(null),
    street: this.fb.control<string | null>(null),
    street2: this.fb.control<string | null>(null),
    city: this.fb.control<string | null>(null),
    state: this.fb.control<string | null>(null),
    zip: this.fb.control<string | null>(null),
    // Set a default value for the country
    country: this.fb.control<string | null>('USA'),
    lat: this.fb.control<number | null>(null),
    lng: this.fb.control<number | null>(null),
    rawText: this.fb.control<string | null>(null),
  });
  onChange: (value: Address | null) => void = () => {};
  onTouched: () => void = () => {};

  // --- Derived State for UI ---
  private readonly selectedCountry = toSignal(
    this.addressForm
      .get('country')!
      .valueChanges.pipe(startWith(this.addressForm.get('country')!.value))
  );

  // ADD computed signals for the zip/postal field
  protected readonly zipLabel = computed(() => {
    return this.selectedCountry() === 'USA' ? 'Zip Code' : 'Postal Code';
  });

  protected readonly zipMask = computed(() => {
    return this.selectedCountry() === 'Canada' ? 'A0A 0A0' : '';
  });

  protected readonly zipPlaceholder = computed(() => {
    const country = this.selectedCountry();
    if (country === 'Canada') return 'A1A 1A1';
    if (country === 'USA') return '12345';
    return 'Enter code';
  });

  private addressValue = signal<Address | null>(null);
  protected readonly formattedAddress: Signal<{ line1: string; line2: string }>;

  protected isDropdownVisible = linkedSignal(() => {
    const hasFocus = this.hasFocus();
    const isLoading = this.isLoading();
    const predictions = this.predictions();
    const streetFormControlValue = this.streetFormControlValueSignal();
    return (
      hasFocus &&
      (streetFormControlValue?.length || -1) > 0 &&
      (isLoading || predictions.length > 0)
    );
  });

  private streetFormControlValueSignal = toSignal(
    this.addressForm.get('street')!.valueChanges,
    { initialValue: '' }
  );

  constructor() {
    // Derive the formatted display string from our manual addressValue signal.
    this.formattedAddress = computed(() => {
      const formValue = this.addressValue(); // This now reads from our manual signal
      if (!formValue || !formValue.street) {
        return { line1: '', line2: '' };
      }
      const { street, street2, city, state, zip } = formValue;
      const line1Parts = [street, street2];
      const line2Parts = [city, state, zip];
      return {
        line1: line1Parts.filter(Boolean).join(', '),
        line2: line2Parts.filter(Boolean).join(', '),
      };
    });

    // 4. Effect to focus input when mobile overlay becomes visible.
    effect(() => {
      if (this.isOverlayVisible() && this.isMobile()) {
        setTimeout(() => this.streetInput?.focusInput(), 0);
      }
    });

    // 4. Add an effect to update states when country changes
    effect(() => {
      const country = this.selectedCountry();
      const countryControl = this.addressForm.get('country');

      const states: State[] =
        ADDRESS_DATA.states[country as keyof typeof ADDRESS_DATA.states] || [];

      // Map the string array to a SelectOption array
      this.stateAndProvinceOptions.set(
        states.map((s) => ({
          label: this.stateDisplayFormat() === 'full' ? s.name : s.code,
          value: s.code, // Always use the short code as the value for consistency.
        }))
      );

      // This logic remains correct
      if (countryControl?.dirty) {
        Promise.resolve().then(() => {
          this.addressForm.get('state')?.reset(null, { emitEvent: false });
        });
      }
    });
  }

  ngOnInit(): void {
    // This subscription handles desktop changes.
    this.subs.add(
      this.addressForm.valueChanges
        .pipe(filter(() => !this.isMobile()))
        .subscribe((value) => {
          // On desktop, any change is a committed change.
          this.onChange(value);
          this.addressValue.set(value); // UPDATE DISPLAY STATE
        })
    );

    // 2. For autocomplete: listen to street input.
    const streetControl = this.addressForm.get('street');
    if (streetControl) {
      this.subs.add(
        streetControl.valueChanges
          .pipe(
            filter(() => !this.isMobile() || this.isOverlayVisible()),
            debounceTime(300),
            distinctUntilChanged(),
            filter(
              (value): value is string =>
                typeof value === 'string' && value.length > 2
            ),
            tap(() => this.isLoading.set(true)),
            switchMap((value) => {
              const tokenPromise = this.sessionToken
                ? Promise.resolve(this.sessionToken)
                : this.googleMapsService.createSessionToken();

              return from(tokenPromise).pipe(
                tap((token) => {
                  // Save the newly created token for subsequent requests.
                  if (!this.sessionToken) {
                    this.sessionToken = token;
                  }
                }),
                switchMap((token) =>
                  this.googleMapsService.getPlacePredictions(value, token)
                )
              );
            })
          )
          .subscribe((predictions) => {
            this.predictions.set(predictions);
            this.isLoading.set(false);
          })
      );
    }
  }

  // --- CVA Methods ---
  writeValue(value: Address | null): void {
    // FIX: Explicitly handle the null case to clear the form.
    if (value) {
      // If we receive a valid address, patch the form with its values.
      this.addressForm.patchValue(value, { emitEvent: false });
    } else {
      // If we receive null, RESET the form to its initial, empty state.
      // We pass the default country value to ensure it's set correctly.
      this.addressForm.reset({ country: 'USA' }, { emitEvent: false });
    }
    // This correctly updates the display signal in both cases.
    this.addressValue.set(value);
  }
  registerOnChange(fn: (value: Address | null) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    this.addressForm[isDisabled ? 'disable' : 'enable']({ emitEvent: false });
  }

  // --- Mobile Experience Methods ---
  protected openMobileOverlay(): void {
    if (!this.isMobile()) return;
    // Save a snapshot of the current state to revert to on cancel.
    this.draftAddress.set(this.addressForm.value);
    this.isOverlayVisible.set(true);
  }

  protected acceptChanges(): void {
    // This is the ONLY place a mobile change is committed.
    const committedValue = this.addressForm.value;
    this.onChange(committedValue);
    this.addressValue.set(committedValue); // UPDATE DISPLAY STATE
    this.onTouched();
    this.isOverlayVisible.set(false);
  }

  protected cancelChanges(): void {
    // Revert the form's value to the saved draft.
    // We do NOT update the display state here, because no change was committed.
    this.addressForm.patchValue(this.draftAddress() || {}, {
      emitEvent: false,
    });
    this.onTouched();
    this.isOverlayVisible.set(false);
  }

  // --- Event Handlers for the UI ---
  protected async onStreetInputFocus(): Promise<void> {
    // --- SESSION START ---

    this.sessionToken = await this.googleMapsService.createSessionToken();
    this.hasFocus.set(true);
  }

  protected onStreetInputBlur(): void {
    // Use a small delay to allow a click on a prediction to register
    // before we turn off the master switch and hide the dropdown.
    setTimeout(() => {
      this.hasFocus.set(false);
      this.isDropdownVisible.set(false);
    }, 150);
  }

  protected async selectPrediction(
    prediction: google.maps.places.PlacePrediction
  ): Promise<void> {
    // --- SESSION END ---
    // 1. Immediately turn off the master switch. This stops the valueChanges pipe.
    this.hasFocus.set(false);
    this.isDropdownVisible.set(false);

    if (!this.sessionToken) return;
    this.isLoading.set(true);

    const placeDetails = await this.googleMapsService.getPlaceDetails(
      prediction
    );

    if (placeDetails) {
      this.populateFormFromPlaceDetails(placeDetails);
    }

    // 2. Clean up the rest of the session state.
    this.isLoading.set(false);
    this.sessionToken = undefined;
    this.predictions.set([]);
  }

  // --- Helper Methods ---
  private populateFormFromPlaceDetails(place: google.maps.places.Place): void {
    const getCountryForForm = (
      googleCountryCode: string | undefined | null
    ): string | undefined => {
      if (!googleCountryCode) return undefined;
      const map: { [key: string]: string } = {
        US: 'USA',
        CA: 'Canada',
        MX: 'Mexico',
      };
      return map[googleCountryCode];
    };

    const address: Address = {
      // FIX: Use the property names from the PlaceResult object (snake_case).
      placeId: place.id,
      street: `${this.getComponent(place, 'street_number')?.longText || ''} ${
        this.getComponent(place, 'route')?.longText || ''
      }`.trim(),
      street2: '',
      city: this.getComponent(place, 'locality')?.longText,
      state: this.getComponent(place, 'administrative_area_level_1')?.shortText,
      zip: this.getComponent(place, 'postal_code')?.longText,
      country: getCountryForForm(
        this.getComponent(place, 'country')?.shortText
      ),
      lat: place.location?.lat(),
      lng: place.location?.lng(),
      rawText: place.formattedAddress,
    };
    this.addressForm.patchValue(address);
  }

  private getComponent(place: google.maps.places.Place, type: string) {
    return place.addressComponents?.find((c) => c.types.includes(type));
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
}
